import {
  useGetExternalDocumentCount,
  useGetExternalDocumentRoleList,
} from '@modusign/openapi-client-external-document';

import useAuthority from '@app/hooks/useAuthority';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import useUsageCount from '@app/hooks/useUsageCount';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useCurrentMemberGroupList, useCurrentUserMemberId } from '@app/hooks/workspace';
import { getNetPermission } from '@app/libs/permission/getNetPermission';

type Permission = {
  type:
    | 'PERMITTED'
    | 'NO_AUTHORITY_PERMISSION'
    | 'NO_ROLE_PERMISSION'
    | 'NO_LEFT_LIMIT'
    | 'USAGE_EXCEEDED';
  isPermitted: boolean;
  message: string;
};
type ExternalDocumentPermission = {
  isLoading: boolean;
  hasAuthority: boolean;
  createPermission: Permission;
  assignPermission: Permission;
};
type ExternalDocumentDetailPermission = ExternalDocumentPermission & {
  updatePermission: Permission;
  deletePermission: Permission;
};

export function useExternalDocumentPermission(
  id?: undefined,
  options?: { enabled: boolean }
): ExternalDocumentPermission;
export function useExternalDocumentPermission(
  id: string,
  options?: { enabled: boolean }
): ExternalDocumentDetailPermission;
export function useExternalDocumentPermission(id?: string, options?: { enabled: boolean }) {
  const workspaceId = useWorkspaceId();
  const authority = useAuthority();
  const hasAuthority = authority.data?.permissions.EXTERNAL_DOCUMENT === true;

  const { permissionMap, isLoading: isRoleLoading } = useMemberPermissions();

  const { externalDocumentCount, isLoading: isUsageLoading } = useUsageCount();
  const externalDocumentUsage = useGetExternalDocumentCount(workspaceId);

  const groups = useCurrentMemberGroupList();
  const memberId = useCurrentUserMemberId();
  const { data } = useGetExternalDocumentRoleList(
    workspaceId,
    id ?? '',
    {},
    { query: { enabled: !!(workspaceId && id) && (options?.enabled ?? true) } }
  );
  const isEditor = data
    ? data.roles.some((rr) => {
        if (rr.name === 'EDITOR') {
          if (rr.subjectType === 'GROUP') {
            return groups.some((g) => g.id === rr.group?.id);
          }
          return rr.member?.id === memberId;
        }

        return false;
      })
    : // data fetch 중일 때는 비관적으로 생각합니다.
      false;

  return {
    isLoading:
      authority.isLoading || isRoleLoading || isUsageLoading || externalDocumentUsage.isLoading,
    hasAuthority,
    createPermission: getNetPermission({
      hasAuthorityPermission: hasAuthority,
      hasRolePermission: permissionMap.CREATE_EXTERNAL_DOCUMENT,
      resourceUsages: [
        {
          limit: externalDocumentCount.limit,
          usages: externalDocumentUsage.data?.count ?? 0,
          resourceName: '외부문서',
        },
      ],
    }),
    assignPermission: getNetPermission({
      hasRolePermission: permissionMap.ASSIGN_EXTERNAL_DOCUMENT,
    }),
    updatePermission: getNetPermission({
      hasAuthorityPermission: hasAuthority,
      hasRolePermission: isEditor || permissionMap.UPDATE_EXTERNAL_DOCUMENT,
    }),
    deletePermission: getNetPermission({
      hasRolePermission: isEditor || permissionMap.DELETE_EXTERNAL_DOCUMENT,
    }),
  };
}
